/**
 * 補正サーバー（live-correct の EC2。Issue #626）の起動状況と、起動・停止（ダッシュボードと「撮影」の画面）。
 *
 * - EC2 の状態は /wake の live_status（Lambda synchro-ec2wake。インスタンスは Name タグ synchro-live で引く）。
 *   **止める ＝ インスタンスごと消す**（EBS を残さない）、**起動 ＝ 素の Ubuntu から作り、起動のときに synchro-client を入れて立てる**（AMI は焼かない。Issue #882）
 * - 動いていれば補正サーバーの /live/info.json も見て、OBS の配信が届いているか・座標（収録の設定で選んだデバイスの座標）があるか・録画中かを出す
 * - **止まっている補正サーバーに OBS が配信を始めると、繋がらずに OBS 31.1 が落ちる**（2026-09-30）ので、
 *   止めるときに配信が届いていれば、先に OBS の配信を止めるよう確かめる
 * - 状態が変わっている途中（pending / stopping）は 3 秒ごと、それ以外は 15 秒ごとに見直す
 * - 起こすときの大きさ（Issue #743）: 撮影用（c7g.2xlarge・8 vCPU。既定）/ 開発用（c7g.xlarge・4 vCPU。動作確認・費用半分）。
 *   動いているものの大きさは状態の横に出す（変えるには止めて起こし直す）
 * - 自動停止: 起こしたときに「72 時間後に消す」予約が入る（22 時をまたぐ収録があるので毎日 22 時には止めない）。
 *   動いているあいだは予約の時刻を出し、「今から N 時間後」に変えられる（live_schedule）
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/api';
import { fetchLiveInfo, isLiveFresh } from './liveCorrect';
import { parseServerTime } from '../utils/serverTime';

const LABELS = {
  running: ['動いています', '#9be7a1'],
  pending: ['起動中…（配れるまで 1〜2 分）', '#f0c36b'],
  stopping: ['停止中…（インスタンスごと消しています）', '#f0c36b'],
  stopped: ['止まっています（起動すると作ります。使えるまで数分）', '#8a93a8'],
};
export const STOP_HOURS = [3, 6, 12, 24, 48, 72, 168];
/** 起こすときの大きさ（Lambda の LIVE_SIZES と同じ鍵） */
export const SIZES = { shoot: '撮影用（8 vCPU）', dev: '開発用（4 vCPU）' };

/** 予約の時刻 → 「10/3 15:59（あと 71 時間）」 */
export function stopText(iso, nowMs = Date.now()) {
  if (!iso) return null;
  const t = parseServerTime(iso);
  if (!t) return null;
  const left = (t.getTime() - nowMs) / 3600000;
  const p = (n) => String(n).padStart(2, '0');
  const when = `${t.getMonth() + 1}/${t.getDate()} ${p(t.getHours())}:${p(t.getMinutes())}`;
  return `${when}（${left >= 1 ? `あと ${Math.floor(left)} 時間` : `あと ${Math.max(0, Math.round(left * 60))} 分`}）`;
}

export function liveServerSummary(state, info, nowSec = Date.now() / 1000) {
  if (state !== 'running') return null;
  if (!info) return '起動処理中（補正サーバーがまだ応答しません）';
  const parts = [isLiveFresh(info, nowSec) ? '配信を受けています' : 'カメラの配信（OBS・TetherCam）は届いていません'];
  // 座標の指紋（cams_hash。Issue #801）
  parts.push(info.cams_hash ? '座標あり' : '座標はまだ');
  if (info.recording) parts.push('録画中');
  return parts.join('・');
}

export default function LiveServerPanel({ compact = false }) {
  const [state, setState] = useState(undefined);
  const [stopAt, setStopAt] = useState(null);
  const [hours, setHours] = useState(72);
  const [size, setSize] = useState('shoot');
  const [runningSize, setRunningSize] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const refresh = useCallback(async () => {
    try {
      const r = await api.liveServer('live_status');
      setState(r.live_state);
      setStopAt(r.scheduled_stop ?? null);
      setRunningSize(r.size ?? null);
      setError(null);
      if (r.live_state === 'running' && typeof fetch === 'function') {
        const i = await fetchLiveInfo();
        // 補正サーバーの info か: cams_hash がある（Issue #801）
        setInfo(i && typeof i === 'object' && 'cams_hash' in i ? i : null);
      } else {
        setInfo(null);
      }
    } catch (e) {
      setError('状態を取れません');
    }
  }, []);

  useEffect(() => {
    let alive = true;
    let timer;
    const loop = async () => {
      await refresh();
      if (!alive) return;
      const s = stateRef.current;
      timer = setTimeout(loop, s === 'pending' || s === 'stopping' ? 3000 : 15000);
    };
    loop();
    return () => { alive = false; clearTimeout(timer); };
  }, [refresh]);

  const act = async (action) => {
    if (action === 'live_stop') {
      const msg = isLiveFresh(info)
        ? 'OBS が配信中です。先に OBS の配信を止めてください（止まった補正サーバーへ配信し直すと OBS が落ちることがあります）。\nそれでも補正サーバーを止めますか？'
        : '補正サーバーを止めますか？';
      if (!window.confirm(msg)) return;
    }
    setBusy(true);
    try {
      const param = action === 'live_start' ? { hours, size } : action === 'live_schedule' ? { hours } : {};
      const r = await api.liveServer(action, param);
      setState(r.live_state);
      if ('size' in r) setRunningSize(r.size ?? null);
      if ('scheduled_stop' in r) setStopAt(r.scheduled_stop ?? null);
      setError(null);
    } catch (e) {
      setError(e?.message && e.message !== 'Live server request failed' ? e.message : '操作できませんでした');
    } finally {
      setBusy(false);
    }
  };

  const [label, color] = state === undefined ? ['確認中…', '#8a93a8'] : LABELS[state] ?? [state, '#8a93a8'];
  const summary = liveServerSummary(state, info);
  return (
    <div className={compact ? undefined : 'card'} data-testid="live-server-panel"
      style={compact ? { fontSize: 13, padding: '8px 10px', border: '1px solid #223', borderRadius: 6, marginBottom: 12 } : { marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong className={compact ? undefined : 'card-title'} style={compact ? undefined : { margin: 0 }}>補正サーバー（ライブ）</strong>
        <span data-testid="live-server-state" style={{ color, fontWeight: 700 }}>● {label}</span>
        {(state === 'running' || state === 'pending') && runningSize ? (
          <span data-testid="live-server-size" style={{ color: '#b8c0d4', fontSize: 12 }}>{SIZES[runningSize] ?? runningSize}</span>
        ) : null}
        <span style={{ flex: 1 }} />
        {state === 'stopped' ? (
          <>
            <select value={size} onChange={(e) => setSize(e.target.value)} disabled={busy} data-testid="live-server-size-select">
              {Object.entries(SIZES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button type="button" className="btn" disabled={busy} onClick={() => act('live_start')} data-testid="live-server-start">起動</button>
          </>
        ) : null}
        {state === 'running' ? (
          <button type="button" className="btn" disabled={busy} onClick={() => act('live_stop')} data-testid="live-server-stop">停止</button>
        ) : null}
        <button type="button" className="btn" disabled={busy} onClick={refresh}>更新</button>
      </div>
      {state === 'running' || state === 'pending' ? (
        <div style={{ marginTop: 6, fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span data-testid="live-server-stop-at" style={{ color: '#b8c0d4' }}>
            自動停止: {stopText(stopAt) ?? '予約なし（配信が 180 分届かなければ止まる）'}
          </span>
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="live-server-hours">
            {STOP_HOURS.map((h) => <option key={h} value={h}>今から {h} 時間後</option>)}
          </select>
          <button type="button" className="btn" disabled={busy} onClick={() => act('live_schedule')} data-testid="live-server-schedule">変更</button>
        </div>
      ) : null}
      {summary ? <div data-testid="live-server-summary" style={{ marginTop: 6, color: '#b8c0d4', fontSize: 12 }}>{summary}</div> : null}
      {error ? <div style={{ marginTop: 6, color: '#ff8a8a', fontSize: 12 }}>{error}</div> : null}
      {!compact && state === 'stopped' ? (
        <div style={{ marginTop: 6, color: '#8a93a8', fontSize: 12 }}>
          止まっている補正サーバーに OBS が配信を始めると OBS が落ちることがあります。収録の前に起動し、「動いています」になってから配信を始めてください。
          起動から 72 時間後に自動で止まります（動いているあいだに変えられます）。
        </div>
      ) : null}
    </div>
  );
}
